import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { extractSubdomainSlug, isTenantHostname } from "@shared/config/tenancy";

export interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  text: string;
}

export interface TenantTheme {
  title: string | null;
  logo: string | null;
  colors: ThemeColors | null;
  restaurantName: string;
  restaurantSlug: string;
}

interface ThemeContextValue {
  theme: TenantTheme | null;
  loading: boolean;
  isTenantAccess: boolean;
  restaurantSlug: string | null;
  setTheme: (theme: TenantTheme | null) => void;
  applyTheme: (colors: ThemeColors) => void;
  resetTheme: () => void;
}

const DEFAULT_COLORS: ThemeColors = {
  primary: '#f97316',
  secondary: '#e44b3a',
  background: '#fdf7f0',
  text: '#1f160f',
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: null,
  loading: true,
  isTenantAccess: false,
  restaurantSlug: null,
  setTheme: () => {},
  applyTheme: () => {},
  resetTheme: () => {},
});

/**
 * Hex-color shape used by every color slot. Matches the server-side
 * `^#[0-9a-fA-F]{6}$` validation on `updateRestaurantTheme`. Reused here as
 * the second-layer guard against the postMessage handler (`THEME_PREVIEW`),
 * which previously accepted arbitrary CSS values like
 * `url(https://attacker.com/...)` because origin-only checks let same-origin
 * XSS popups push CSS values straight to `style.setProperty`.
 */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const isValidHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR.test(value);

const isValidThemeColors = (value: unknown): value is ThemeColors => {
  if (!value || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return (
    isValidHexColor(c.primary) &&
    isValidHexColor(c.secondary) &&
    isValidHexColor(c.background) &&
    isValidHexColor(c.text)
  );
};

function applyCssVariables(colors: ThemeColors) {
  const root = document.documentElement;
  root.style.setProperty('--theme-primary', colors.primary);
  root.style.setProperty('--theme-secondary', colors.secondary);
  root.style.setProperty('--theme-background', colors.background);
  root.style.setProperty('--theme-text', colors.text);
}

function removeCssVariables() {
  const root = document.documentElement;
  root.style.removeProperty('--theme-primary');
  root.style.removeProperty('--theme-secondary');
  root.style.removeProperty('--theme-background');
  root.style.removeProperty('--theme-text');
}

export function ThemeProvider({ children, initialTenantSlug }: { children: ReactNode; initialTenantSlug?: string }) {
  const [theme, setTheme] = useState<TenantTheme | null>(null);
  const [loading, setLoading] = useState(true);

  // No SSR: usa initialTenantSlug passado pelo servidor (via hostname do request).
  // No cliente: lê window.location.hostname diretamente.
  const [isTenantAccess, setIsTenantAccess] = useState<boolean>(() => {
    if (typeof window === 'undefined') return !!initialTenantSlug;
    return isTenantHostname(window.location.hostname);
  });
  const [tenantSlug, setTenantSlug] = useState<string | null>(() => {
    if (typeof window === 'undefined') return initialTenantSlug ?? null;
    return extractSubdomainSlug(window.location.hostname);
  });

  const applyTheme = useCallback((colors: ThemeColors) => {
    if (typeof window !== 'undefined') {
      applyCssVariables(colors);
    }
  }, []);

  const resetTheme = useCallback(() => {
    if (typeof window !== 'undefined') {
      removeCssVariables();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'THEME_PREVIEW') return;
      // Validate payload shape — origin check alone is insufficient when a
      // same-origin XSS popup (see OrderReceipt escape) could craft messages.
      // Each color slot must match the same hex regex the server enforces.
      if (!isValidThemeColors(event.data.colors)) return;
      applyCssVariables(event.data.colors);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const endpoint = process.env.GRAPHQL_ENDPOINT;
    if (!endpoint) {
      setLoading(false);
      return;
    }

    const hostname = window.location.hostname;

    if (!isTenantHostname(hostname)) {
      setLoading(false);
      return;
    }

    let queryVariables: { domain?: string; slug?: string } = {};

    const slug = extractSubdomainSlug(hostname);
    if (slug) {
      queryVariables.slug = slug;
      setIsTenantAccess(true);
      setTenantSlug(slug);
    } else {
      // Domínio customizado — não temos o slug ainda, só o domain
      queryVariables.domain = hostname;
      setIsTenantAccess(true);
    }

    const query = `query TenantTheme($domain: String, $slug: String) {
      tenantTheme(domain: $domain, slug: $slug) {
        title
        logo
        colors { primary secondary background text }
        restaurantName
        restaurantSlug
      }
    }`;

    fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query, variables: queryVariables }),
    })
      .then((res) => res.json())
      .then((data) => {
        const tenantTheme = data?.data?.tenantTheme;
        if (tenantTheme) {
          setTheme(tenantTheme);
          if (tenantTheme.restaurantSlug) {
            setTenantSlug(tenantTheme.restaurantSlug);
          }
          if (tenantTheme.colors) {
            applyCssVariables(tenantTheme.colors);
          } else {
            // Tema customizado desativado globalmente — aplica as cores padrão do LeCartte
            applyCssVariables(DEFAULT_COLORS);
          }
        }
      })
      .catch(() => {
        // Silently fail - use default theme
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, loading, isTenantAccess, restaurantSlug: tenantSlug, setTheme, applyTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

export { DEFAULT_COLORS };
